import React, { Component } from 'react';
import { connect } from 'react-redux';
import { debounce, isEqual, get } from 'lodash';

import SplitPurchaseTotalCost from '../components/split-purchase.total-cost';
import SplitPurchasePerSlot from '../components/split-purchase.per-slot';
import { Money } from '../lib/money';
import Purchase from '../lib/purchase';
import Membership from '../lib/membership';
import { getURLParams } from '../lib/functions';

import {
  EVEN_SPLIT,
  FIXED_PER_PERSON,
} from '../lib/split-types';
import {
  projectPurchase,
} from '../actions';
import {
  getCurrentUserMembership,
  getCommittedMemberships,
} from '../selectors/purchase-selector';

class SplitPurchase extends Component {
  constructor(props) {
    super(props);

    this.state = {
      splitType: props.splitType,
      minSlots: props.initialMinSlot,
      maxSlots: props.maxSlots,
      minContribution: props.minContribution || Money.zero(props.purchase.currencyCode),
    };

    this.onMinSlotsChange = this.onMinSlotsChange.bind(this);
    this.onMaxSlotsChange = this.onMaxSlotsChange.bind(this);
    this.onSplitTypeChange = this.onSplitTypeChange.bind(this);
    this.onMinContributionChange = this.onMinContributionChange.bind(this);
    this.debouncedProjectPurchase = debounce(props.projectPurchase, 500, { maxWait: 2000 });
  }

  componentWillMount() {
    this.fetchProjections();
  }

  componentDidUpdate(prevProps, prevState) {
    const newCurrentMember = prevProps.currentMembershipId !== this.props.currentMembershipId;

    if (newCurrentMember || this.projectionValuesChanged(prevState)) {
      this.fetchProjections();
    }
  }

  get hasCommittedMemberships() {
    return get(this.props, 'committedMemeberships', []).length > 0;
  }

  onMinSlotsChange(minSlots) {
    if (minSlots === '') return;
    this.setState({ minSlots });
    this.fetchProjections();
  }

  onMaxSlotsChange(maxSlots) {
    this.setState({ maxSlots });
    this.fetchProjections();
  }

  onSplitTypeChange(splitType) {
    this.setState({ splitType });
    this.fetchProjections();
  }

  onMinContributionChange(minContribution) {
    this.setState({ minContribution });
    this.fetchProjections();
  }

  projectionValuesChanged(prevState) {
    const changed = key => !isEqual(this.state[key], prevState[key]);

    return changed('splitType') || changed('minSlots') || changed('maxSlots') || changed('showMaxSlots') ||
      changed('minContribution');
  }

  fetchProjections() {
    const { currentMembership, currentMembershipId, purchase } = this.props;
    const group = {
      splitType: this.state.splitType,
    };
    const data = {
      id: purchase.id,
      memberships: [],
    };
    let allowedShareAmount = !currentMembership ? this.state.minContribution :
      currentMembership.allowedShareAmount;

    if (Purchase.isSpecifiedPerPerson(this.state.splitType)) {
      group.minContribution = this.state.minContribution;
    } else {
      group.minSlots = this.state.minSlots;
      group.maxSlots = this.state.maxSlots;
    }

    data.group = group;

    if (!this.hasCommittedMemberships || !currentMembership ||
      Membership.isCommitted(currentMembership.status)) {
      data.memberships.push(
        {
          id: currentMembershipId,
          claimedSlots: 1,
          allowedShareAmount,
        },
      );
    }

    this.debouncedProjectPurchase(data, this.props.namespace);
  }

  isTotalCost() {
    return Purchase.isTotal(this.props.costType);
  }

  render() {
    if (this.isTotalCost()) {
      return (
        <SplitPurchaseTotalCost
          currencyCode={this.props.currencyCode}
          splitType={this.props.splitType}
          minSlots={this.props.minSlots}
          maxSlots={this.props.maxSlots}
          groupMaxSlots={this.props.groupMaxSlots}
          onMinSlotsChange={this.onMinSlotsChange}
          onMaxSlotsChange={this.onMaxSlotsChange}
          onMinContributionChange={this.onMinContributionChange}
          onSplitTypeChange={this.onSplitTypeChange}
          showMaxSlots={this.props.showMaxSlots}
          namespace={this.props.namespace}
          slotName={this.props.purchase.slotName || 'share'}
          selectedSlots={this.state.minSlots}
        />
      );
    }

    return (
      <SplitPurchasePerSlot
        minSlots={this.props.minSlots}
        maxSlots={this.props.maxSlots}
        groupMaxSlots={this.props.groupMaxSlots}
        onMinSlotsChange={this.onMinSlotsChange}
        onMaxSlotsChange={this.onMaxSlotsChange}
        showMaxSlots={this.props.showMaxSlots}
        slotName={this.props.purchase.slotName}
        selectedSlots={this.state.minSlots}
      />
    );
  }
}

SplitPurchase.defaultProps = {
  showMaxSlots: false,
};

const mapStateToProps = (state) => {
  const qsFormValues = getURLParams();

  const { purchases, memberships } = state;
  const purchase = purchases.byId[purchases.current];

  const costType = purchase.product.costType;
  const defaultSplitType = Purchase.isPerSlot(costType) ? FIXED_PER_PERSON : EVEN_SPLIT;

  const initialMinSlot = parseInt(qsFormValues.minSlots, 10)
    || purchase.group.minSlots
    || (purchase.product.minSlots === 1 ? 2 : purchase.product.minSlots);

  const currentMembership = getCurrentUserMembership(state);

  return {
    currentMembershipId: memberships.current,
    committedMemeberships: getCommittedMemberships(state),
    currentMembership,
    purchase,
    costType: purchase.product.costType,
    currencyCode: purchase.currencyCode,
    minSlots: purchase.product.minSlots,
    maxSlots: purchase.product.maxSlots,
    splitType: purchase.group.splitType || defaultSplitType,
    minContribution: purchase.group.minContribution,
    groupMaxSlots: !!purchase.group.maxSlots,
    initialMinSlot,
  };
};

const mapDispatchToProps = {
  projectPurchase,
};

export default connect(mapStateToProps, mapDispatchToProps)(SplitPurchase);




// WEBPACK FOOTER //
// ./src/desktop/containers/split-purchase.container.js